<script setup>
import { ref, watch, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "../services/Store";
import Cookies from "js-cookie";

const showModal = ref(false);
const userStore = useUserStore();
const route = useRoute();

// Admin menu: a left icon rail like the users' dashboard (UserLayouts.vue),
// in groups separated by a thin line. "label" is what fits under the icon;
// "name" shows on hover.
const groups = [
  [{ name: "Dashboard", path: "/dashboard", icon: "dashboard" }],
  [
    { name: "Users", path: "/user", icon: "users" },
    { name: "Agents", path: "/agent", icon: "agents" },
    { name: "Website", path: "/website", icon: "globe" },
    { name: "Publish Articles", label: "Articles", path: "/publish-articles", icon: "articles" },
  ],
  [
    { name: "Subscriptions", path: "/subscriptions", icon: "repeat" },
    { name: "Plans", path: "/plan", icon: "layers" },
    { name: "Add-Ons", path: "/addons", icon: "puzzle" },
    { name: "Invoices", path: "/invoices", icon: "receipt" },
    { name: "Commissions", path: "/commissions", icon: "coins" },
    { name: "AI Usage", path: "/ai-usage", icon: "sparkles" },
  ],
  [
    { name: "Our Sites", path: "/our-sites", icon: "chart" },
    { name: "Prospecting", path: "/prospecting", icon: "target" },
    { name: "Subscribers", path: "/subscribers", icon: "mail" },
    { name: "Our Chatbot", label: "Chatbot", path: "/our-chatbot", icon: "chat" },
  ],
  [{ name: "Changelog", path: "/changelog", icon: "clock" }],
];

// Outline icons, 24x24, drawn with the current text color.
const ICONS = {
  dashboard: '<rect x="3.5" y="3.5" width="7" height="8" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="5" rx="1.5"/><rect x="13.5" y="11.5" width="7" height="9" rx="1.5"/><rect x="3.5" y="14.5" width="7" height="6" rx="1.5"/>',
  users: '<circle cx="9" cy="8" r="3.5"/><path stroke-linecap="round" d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><path stroke-linecap="round" d="M15.5 4.8a3.5 3.5 0 010 6.4M18 14.8c1.9.8 3.1 2.6 3.5 5.2"/>',
  agents: '<circle cx="12" cy="8" r="3.5"/><path stroke-linecap="round" d="M5 20.5c.7-3.6 3.5-6 7-6s6.3 2.4 7 6"/><path stroke-linecap="round" stroke-linejoin="round" d="M12 14.5l-1.2 2.3L12 20l1.2-3.2z"/>',
  globe: '<circle cx="12" cy="12" r="9"/><path stroke-linecap="round" d="M3 12h18M12 3c2.5 2.7 3.8 6 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-6-3.8-9s1.3-6.3 3.8-9z"/>',
  articles: '<rect x="4.5" y="3.5" width="13" height="17" rx="1.5"/><path stroke-linecap="round" d="M8 8h6M8 11.5h6M8 15h4"/>',
  repeat: '<path stroke-linecap="round" stroke-linejoin="round" d="M17 3.5l3 3-3 3"/><path stroke-linecap="round" stroke-linejoin="round" d="M20 6.5H8.5A4.5 4.5 0 004 11v1"/><path stroke-linecap="round" stroke-linejoin="round" d="M7 20.5l-3-3 3-3"/><path stroke-linecap="round" stroke-linejoin="round" d="M4 17.5h11.5A4.5 4.5 0 0020 13v-1"/>',
  layers: '<path stroke-linejoin="round" d="M12 3.5l8.5 4.5L12 12.5 3.5 8z"/><path stroke-linecap="round" stroke-linejoin="round" d="M3.5 12L12 16.5 20.5 12M3.5 16L12 20.5 20.5 16"/>',
  puzzle: '<path stroke-linejoin="round" d="M9 4.5a2 2 0 014 0V6h3.5a1 1 0 011 1v3.5H19a2 2 0 010 4h-1.5V18a1 1 0 01-1 1H13v-1.5a2 2 0 00-4 0V19H5.5a1 1 0 01-1-1v-3.5H6a2 2 0 000-4H4.5V7a1 1 0 011-1H9z"/>',
  receipt: '<path stroke-linecap="round" stroke-linejoin="round" d="M6 3.75h9L19.5 8.25V20a.75.75 0 01-.75.75H6a.75.75 0 01-.75-.75V4.5a.75.75 0 01.75-.75z"/><path stroke-linecap="round" d="M9 12h6M9 15.5h6M9 8.5h3"/>',
  coins: '<ellipse cx="9.5" cy="7" rx="5.5" ry="2.5"/><path d="M4 7v4c0 1.4 2.5 2.5 5.5 2.5S15 12.4 15 11V7"/><path d="M4 11v4c0 1.4 2.5 2.5 5.5 2.5 1 0 1.9-.1 2.7-.3"/><circle cx="16.5" cy="16" r="4"/><path stroke-linecap="round" d="M16.5 14.3v3.4"/>',
  sparkles: '<path stroke-linejoin="round" d="M10 3.5l1.6 4.4L16 9.5l-4.4 1.6L10 15.5l-1.6-4.4L4 9.5l4.4-1.6z"/><path stroke-linejoin="round" d="M17.5 14l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z"/>',
  chart: '<path stroke-linecap="round" stroke-linejoin="round" d="M3.5 3.5v17h17"/><path stroke-linecap="round" stroke-linejoin="round" d="M7 15l3.5-4 3 2.5L19 7"/><path stroke-linecap="round" stroke-linejoin="round" d="M15.5 7H19v3.5"/>',
  target: '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>',
  mail: '<rect x="3" y="5.5" width="18" height="13" rx="2"/><path stroke-linecap="round" stroke-linejoin="round" d="M3.5 7l8.5 6 8.5-6"/>',
  chat: '<path stroke-linejoin="round" d="M4 5.5A1.5 1.5 0 015.5 4h13A1.5 1.5 0 0120 5.5v9a1.5 1.5 0 01-1.5 1.5H10l-4.5 4v-4h0A1.5 1.5 0 014 14.5z"/><path stroke-linecap="round" d="M8.5 10h.01M12 10h.01M15.5 10h.01"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5V12l3 2"/>',
};

// Highlighted for the page itself and its sub-pages (e.g. /prospecting/123),
// except Dashboard, whose sub-page /dashboard/profile is "My profile".
const isActive = (p) => route.path === p.path || (p.path !== "/dashboard" && route.path.startsWith(`${p.path}/`));

// On short screens the menu scrolls; keep the current page's item in view.
const rail = ref(null);
const showActive = async () => {
  await nextTick();
  rail.value?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest" });
};
onMounted(showActive);
watch(() => route.path, showActive);

const logout = () => {
  userStore.clearUser();
  Cookies.remove("authToken", { path: "/" });
  window.location.href = "/";
  // Same fix as UserLayouts.vue's logout() — preserve tourSeen:* keys
  // (the client-side "has this user seen the onboarding tour" flag)
  // instead of blanket-clearing localStorage, so the tour doesn't replay
  // on every login for a user who also has admin access.
  Object.keys(localStorage)
    .filter((key) => !key.startsWith("tourSeen:"))
    .forEach((key) => localStorage.removeItem(key));
  sessionStorage.clear();
};
</script>

<template>
  <div class="flex min-h-[100dvh]">
    <!-- Left icon rail (same style as the users' dashboard) -->
    <aside class="w-[84px] flex-shrink-0 bg-white border-r border-gray-100 flex flex-col items-center sticky top-0 h-[100dvh] z-50">
      <router-link to="/dashboard" class="pt-3 pb-1" title="Dashboard">
        <img src="/images/logo-icon.svg" alt="SEO on demand" class="w-7 h-7" />
      </router-link>
      <div class="text-[9px] font-semibold tracking-wide uppercase text-[#40008A] mb-1">Admin</div>

      <nav ref="rail" class="admin-rail flex-1 overflow-y-auto w-full px-1.5 pb-3 flex flex-col items-center gap-0.5" aria-label="Admin menu">
        <template v-for="(group, gi) in groups" :key="gi">
          <div v-if="gi" class="w-8 my-1 border-t border-gray-100 shrink-0"></div>
          <router-link
            v-for="page in group"
            :key="page.path"
            :to="page.path"
            :title="page.name"
            :aria-current="isActive(page) ? 'page' : undefined"
            class="w-full flex flex-col items-center gap-0.5 px-0.5 py-1 rounded-lg text-center transition-colors shrink-0"
            :class="isActive(page) ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50'"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              class="w-[18px] h-[18px] shrink-0"
              :class="isActive(page) ? 'text-[#40008A]' : ''"
              aria-hidden="true"
              v-html="ICONS[page.icon]"
            ></svg>
            <span class="text-[10px] leading-tight">{{ page.label || page.name }}</span>
          </router-link>
        </template>
      </nav>
    </aside>

    <!-- Main column -->
    <div class="flex flex-col flex-1 min-w-0">
      <div class="flex justify-end items-center py-2 px-4 sticky top-0 z-[45] bg-white shadow">
      <div class="flex gap-2">
        <div class="relative">
          <div
            class="absolute size-1 rounded-full top-2 right-1.5 bg-red-600"
          ></div>
          <svg
            width="37"
            height="37"
            viewBox="0 0 37 37"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.910156"
              y="0.626953"
              width="35.2407"
              height="35.84"
              rx="8"
              fill="#FFFAF1"
            />
            <path
              d="M25.6327 23.4403C25.1607 23.0124 24.7475 22.5218 24.4042 21.9818C24.0293 21.2364 23.8047 20.4224 23.7434 19.5875V17.1285C23.7466 15.8171 23.2789 14.5497 22.4281 13.5644C21.5773 12.579 20.402 11.9436 19.123 11.7774V11.1352C19.123 10.959 19.0541 10.79 18.9316 10.6653C18.809 10.5407 18.6428 10.4707 18.4695 10.4707C18.2962 10.4707 18.13 10.5407 18.0075 10.6653C17.885 10.79 17.8161 10.959 17.8161 11.1352V11.7873C16.5486 11.9655 15.3874 12.6048 14.5478 13.5868C13.7082 14.5689 13.2469 15.8271 13.2495 17.1285V19.5875C13.1882 20.4224 12.9636 21.2364 12.5887 21.9818C12.2515 22.5206 11.8449 23.0111 11.3798 23.4403C11.3276 23.4869 11.2857 23.5444 11.257 23.6087C11.2284 23.6731 11.2135 23.743 11.2134 23.8136V24.4906C11.2134 24.6226 11.2649 24.7492 11.3567 24.8426C11.4485 24.9359 11.573 24.9884 11.7028 24.9884H25.3096C25.4395 24.9884 25.564 24.9359 25.6557 24.8426C25.7475 24.7492 25.7991 24.6226 25.7991 24.4906V23.8136C25.799 23.743 25.7841 23.6731 25.7554 23.6087C25.7267 23.5444 25.6849 23.4869 25.6327 23.4403ZM12.2314 23.9928C12.6868 23.5454 13.0878 23.0441 13.4257 22.4995C13.8978 21.5993 14.1733 20.606 14.2333 19.5875V17.1285C14.2139 16.5451 14.3101 15.9637 14.5162 15.4189C14.7224 14.8742 15.0342 14.3772 15.4331 13.9575C15.832 13.5378 16.3099 13.2041 16.8383 12.9762C17.3667 12.7482 17.9347 12.6308 18.5087 12.6308C19.0826 12.6308 19.6507 12.7482 20.1791 12.9762C20.7075 13.2041 21.1854 13.5378 21.5843 13.9575C21.9832 14.3772 22.295 14.8742 22.5011 15.4189C22.7073 15.9637 22.8035 16.5451 22.7841 17.1285V19.5875C22.8441 20.606 23.1195 21.5993 23.5917 22.4995C23.9296 23.0441 24.3305 23.5454 24.7859 23.9928H12.2314Z"
              fill="#40008A"
            />
            <path
              d="M18.5305 26.6506C18.8388 26.6433 19.1347 26.5255 19.3658 26.3178C19.5969 26.1101 19.7483 25.8259 19.7933 25.5156H17.2188C17.265 25.8344 17.4235 26.1252 17.6648 26.3339C17.9061 26.5427 18.2137 26.6552 18.5305 26.6506Z"
              fill="#40008A"
            />
          </svg>
        </div>

        <div class="flex flex-wrap items-center justify-between">
          <div class="relative">
            <button
              class="flex items-center whitespace-nowrap cursor-pointer"
              @click="showModal = !showModal"
              type="button"
            >
              <img
                :src="
                  userStore?.user?.avatar ||
                  'https://tecdn.b-cdn.net/img/Photos/Avatars/img (31).webp'
                "
                class="rounded-full border-2 border-[#40008A]/30 object-cover flex-shrink-0"
                style="height: 36px; width: 36px"
                alt="Avatar"
                loading="lazy"
              />
              <div class="px-2 flex flex-col min-w-0" style="max-width: 140px;">
                <p
                  class="text-[12px] truncate font-[600] text-gray-800"
                  :title="userStore.user?.name"
                >
                  {{ userStore.user?.name }}
                </p>
                <label class="text-[10px] capitalize text-gray-500">
                  {{ userStore.user?.role }}</label
                >
              </div>
              <span class="[&>svg]:w-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </button>
            <ul
              class="dark:bg-surfa absolute left-0 right-0 top-12 z-[1000] float-left m-0 list-none rounded-lg border-none bg-white bg-clip-padding text-left text-base shadow-lg dark:bg-surface-dark overflow-hidden "
              :class="showModal ? 'block' : 'hidden'"
            >
              <li>
                <router-link
                  class="block w-full whitespace-nowrap bg-transparent px-4 py-2 text-sm font-normal text-neutral-700 hover:bg-[#40008A] hover:text-white"
                  to="/dashboard/profile"
                  data-twe-dropdown-item-ref
                  >My profile</router-link
                >
              </li>
              <li>
                <p
                  class="block w-full whitespace-nowrap bg-transparent px-4 py-2 text-sm font-normal text-neutral-700 hover:bg-[#40008A] hover:text-white cursor-pointer"
                  @click="logout"
                >
                  Logout
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      </div>
      <div class="bg-[#F2F1EC] grow relative p-4">
        <div class="relative z-40">
          <slot></slot>
        </div>

        <img src="/images/authEllipse.png" alt="" class="authEllipse" />
        <img src="/images/authEllipseTop.png" alt="" class="authEllipseTop" />
        <img src="/images/dashboardLines.png" alt="" class="authEllipseTop" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.authEllipse {
  position: absolute;
  width: 100%;
  height: 100%;
  bottom: 0;
  left: 0;
  z-index: 10;
  pointer-events: none;
}
.authEllipseTop {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  z-index: 10;
  pointer-events: none;
}
/* Thin scrollbar on the rail when the screen is short. */
.admin-rail {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}
</style>